import Info from "@mui/icons-material/Info";
import { Box, Stack, Typography } from "@mui/material";
import { Controller } from "react-hook-form";
import {
  DocumentType,
  PaymentStatus,
  FacilityDocumentInput,
} from "generated-graphql/graphql";
import { EditMode } from "components/Document";
import { PaymentDocumentsTable } from "../PaymentDocumentsTable";
import { AttachmentButton } from "../AttachmentButton";
import { PayeeFormInputs } from "../lib/types";
import { usePaymentDocumentData, usePaymentDocumentDeletion } from "../lib";
import {
  Control,
  UseFormWatch,
  UseFormSetValue,
  FieldArrayWithId,
  UseFieldArrayRemove,
} from "react-hook-form";

interface InvoicesAttachmentsSectionProps {
  control: Control<PayeeFormInputs>;
  paymentId?: string;
  existingPayment?: any;
  isLob: boolean;
  notEditable: boolean;
  tenantId?: string;
  facilityId?: string;
  reportId?: string;
  activityId?: string;
  fields: FieldArrayWithId<PayeeFormInputs, "paymentInvoices", "id">[];
  remove: UseFieldArrayRemove;
  watch: UseFormWatch<PayeeFormInputs>;
  setValue: UseFormSetValue<PayeeFormInputs>;
  hasUnsavedChanges: boolean;
  setAddEditDocumentDialogState: (state: any) => void;
  deleteDocument: any;
}

export function InvoicesAttachmentsSection({
  control,
  paymentId,
  existingPayment,
  isLob,
  notEditable,
  tenantId,
  facilityId,
  reportId,
  activityId,
  fields,
  remove,
  watch,
  setValue,
  hasUnsavedChanges,
  setAddEditDocumentDialogState,
  deleteDocument,
}: InvoicesAttachmentsSectionProps) {
  // Create facility associations for document upload
  const documentFacilities: FacilityDocumentInput[] = [];

  // Add the primary facility if it exists
  if (facilityId) {
    documentFacilities.push({ id: facilityId });
  }

  // Use our new hooks to normalize data and handle deletion
  const paymentData = usePaymentDocumentData({
    paymentId,
    existingPayment,
    formFields: fields,
    watch,
  });

  const { handleDocumentDeletion, allowDeletion } = usePaymentDocumentDeletion({
    paymentId,
    existingPayment,
    formFields: fields,
    remove,
    setValue,
    watch,
    deleteDocument,
    hasUnsavedChanges,
  });

  // Determine if we should show attachment buttons
  const showAttachmentButtons =
    !paymentId ||
    (paymentId &&
      existingPayment &&
      existingPayment.status !== PaymentStatus.Pending);

  // Determine receipt button visibility for new LOB payments
  const showReceiptButton = !isLob || !!existingPayment;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Invoices & Attachments
      </Typography>

      {/* Validation errors for new payments */}
      {!paymentId && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Controller
            name="paymentInvoices"
            control={control}
            render={({ fieldState: { error: invoiceError } }) => (
              <Controller
                name="receiptDocument"
                control={control}
                render={({ fieldState: { error: receiptError } }) => {
                  const errors = [];
                  if (invoiceError?.message)
                    errors.push("An invoice is required");
                  if (receiptError?.message)
                    errors.push("a receipt is required");
                  if (errors.length > 0) {
                    return (
                      <Typography
                        variant="body2"
                        color="error"
                        sx={{ fontStyle: "italic" }}
                      >
                        ({errors.join(", ")})
                      </Typography>
                    );
                  }
                  return <></>;
                }}
              />
            )}
          />
        </Stack>
      )}

      {/* Attachment buttons */}
      {showAttachmentButtons && (
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <AttachmentButton
            documentType={DocumentType.Invoice}
            isExistingPayment={!!paymentId}
            hasExistingDocument={
              !!(existingPayment?.paymentInvoices?.length > 0)
            }
            hasFormDocument={fields.length > 0}
            tenantId={tenantId}
            disabled={notEditable}
            onAttach={() =>
              setAddEditDocumentDialogState((state: any) => ({
                ...state,
                open: true,
                mode: EditMode.ADD,
                tenantId,
                facilities: documentFacilities,
                reportId,
                activityId,
                documentType: DocumentType.Invoice,
                description: "",
              }))
            }
          />

          {showReceiptButton && (
            <AttachmentButton
              documentType={DocumentType.Receipt}
              isExistingPayment={!!paymentId}
              hasExistingDocument={!!existingPayment?.document}
              hasFormDocument={!!watch("receiptDocument")}
              tenantId={tenantId}
              disabled={notEditable}
              onAttach={() =>
                setAddEditDocumentDialogState((state: any) => ({
                  ...state,
                  open: true,
                  mode: EditMode.ADD,
                  tenantId,
                  facilities: documentFacilities,
                  reportId,
                  activityId,
                  documentType: DocumentType.Receipt,
                  description: "",
                }))
              }
            />
          )}
        </Stack>
      )}

      {/* Pending documents warning */}
      {hasUnsavedChanges && (
        <Box
          sx={{
            p: 1.5,
            mb: 2,
            bgcolor: "rgba(25, 118, 210, 0.06)",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "rgba(25, 118, 210, 0.2)",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Info sx={{ color: "rgba(25, 118, 210, 0.7)" }} fontSize="small" />
            <Typography
              variant="body2"
              sx={{ color: "rgba(25, 118, 210, 0.87)" }}
            >
              <strong>
                {(() => {
                  const pendingCount =
                    (fields.length > 0 ? fields.length : 0) +
                    (watch("receiptDocument") ? 1 : 0);

                  if (fields.length > 0 && watch("receiptDocument")) {
                    return `${fields.length} invoice(s) and 1 receipt`;
                  } else if (fields.length > 0) {
                    return `${fields.length} invoice(s)`;
                  } else {
                    return "1 receipt";
                  }
                })()}
              </strong>{" "}
              pending. Click "{!paymentId ? "Submit Payment" : "Save Payment"}"
              to{" "}
              {!paymentId
                ? "create payment with these documents"
                : "save changes"}
              .
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Unified documents table */}
      <PaymentDocumentsTable
        payment={paymentData}
        allowDeletion={allowDeletion}
        hideTitle={true}
        onRemoveDocument={allowDeletion ? handleDocumentDeletion : undefined}
      />
    </Box>
  );
}
